import { v4 as uuid } from 'uuid';
import type { IMetaModel } from '../../../../stores/model/metamodel';
import type { IObjectType } from '../../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../../stores/model/metamodel/method';
import type { IDependency } from '../../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../../stores/model/metamodel/inheritance';
import type { IDataType } from '../../../../stores/model/metamodel/data_type';
import type { IFSM } from '../../../../stores/model/metamodel/fsm';
import type { IState } from '../../../../stores/model/metamodel/state';
import type { ITransition } from '../../../../stores/model/metamodel/transition';
import type { IAttribute } from '../../../../stores/model/metamodel/attribute';
import type { IMultiplePropagationConstraint } from '../../../../stores/model/metamodel/multiple_propagation_constraint';
import type {
  AttributeOwnerLike,
  DependencyName,
  DependencyType,
  FSMType,
  MethodType,
  MPCType,
  StateType,
} from '../../types';

/**
 * Atomically create elements.
 *
 * These methods generate a uuid for the id and then forward to the MST actions.
 * The ids are generated here and not in the MST actions because otherwise those
 * actions are not deterministic anymore and we cannot properly replay them
 * (ids for references wouldn't match anymore).
 */

export function ACMC_Object(
  metamodel: IMetaModel,
  name: string,
  description: string,
  abstract: boolean
): IObjectType {
  return metamodel.addObjectType(uuid(), name, description, abstract);
}

export function ACMC_Event(metamodel: IMetaModel, name: string): IEventType {
  return metamodel.addEventType(uuid(), name);
}

export function ACME_SpecialisedEvent(
  metamodel: IMetaModel,
  name: string,
  ownerEvent: IEventType,
  inheritance: IInheritance
): IEventType {
  return metamodel.addEventType(uuid(), name, ownerEvent.id, inheritance.id);
}

export function ACMC_OwnedMethod(
  metamodel: IMetaModel,
  name: string,
  ownerObject: IObjectType,
  ownerEvent: IEventType,
  type: MethodType
): IMethod {
  return metamodel.addMethod(uuid(), name, ownerObject.id, ownerEvent.id, 'OWNED', type);
}

export function ACMC_AcquiredMethod(
  metamodel: IMetaModel,
  name: string,
  ownerObject: IObjectType,
  ownerEvent: IEventType,
  type: MethodType,
  viaDependency: IDependency,
  viaMethod: IMethod
): IMethod {
  return metamodel.addMethod(
    uuid(),
    name,
    ownerObject.id,
    ownerEvent.id,
    'ACQUIRED',
    type,
    viaDependency.id,
    viaMethod.id
  );
}

export function ACMC_InheritedMethod(
  metamodel: IMetaModel,
  name: string,
  ownerObject: IObjectType,
  ownerEvent: IEventType,
  type: MethodType,
  viaMethod: IMethod,
  viaInheritance: IInheritance
): IMethod {
  return metamodel.addMethod(
    uuid(),
    name,
    ownerObject.id,
    ownerEvent.id,
    'INHERITED',
    type,
    null,
    viaMethod.id,
    viaInheritance.id
  );
}

export function ACMC_SpecialisedMethod(
  metamodel: IMetaModel,
  name: string,
  ownerObject: IObjectType,
  ownerEvent: IEventType,
  type: MethodType,
  viaMethod: IMethod
): IMethod {
  return metamodel.addMethod(
    uuid(),
    name,
    ownerObject.id,
    ownerEvent.id,
    'SPECIALISED',
    type,
    null,
    viaMethod.id
  );
}

export function ACMC_Dependency(
  metamodel: IMetaModel,
  name: DependencyName,
  master: IObjectType,
  dependent: IObjectType,
  type: DependencyType
): IDependency {
  return metamodel.addDependency(uuid(), name, master.id, dependent.id, type);
}

export function ACMC_Inheritance(
  metamodel: IMetaModel,
  supertype: IObjectType,
  subtype: IObjectType
): IInheritance {
  return metamodel.addInheritance(uuid(), supertype.id, subtype.id);
}

export function ACMC_DataType(metamodel: IMetaModel, name: string): IDataType {
  return metamodel.addDataType(uuid(), name);
}

export function ACMC_Attribute(
  owner: AttributeOwnerLike,
  name: string,
  type: string,
  description: string,
  implementation: string,
  validation: string
): IAttribute {
  return owner.addAttribute(uuid(), name, type, description, implementation, validation);
}

export function ACMC_MultiplePropagationConstraint(
  object: IObjectType,
  type: MPCType,
  primaryPath: string[],
  secondaryPath: string[]
): IMultiplePropagationConstraint {
  return object.addMultiplePropagationConstraint(uuid(), type, [
    {
      id: uuid(),
      primary: true,
      elements: primaryPath,
    },
    {
      id: uuid(),
      primary: false,
      elements: secondaryPath,
    },
  ]);
}

export function ACMC_FSM(
  object: IObjectType,
  name: string,
  type: FSMType,
  codegeneration: boolean
): IFSM {
  return object.addFSM(uuid(), name, type, codegeneration);
}

export function ACMC_State(fsm: IFSM, name: string, type: StateType): IState {
  return fsm.addState(uuid(), name, type);
}

export function ACMC_Transition(
  fsm: IFSM,
  name: string,
  sourceState: IState,
  targetState: IState,
  methods: IMethod[]
): ITransition {
  return fsm.addTransition(
    uuid(),
    name,
    sourceState.id,
    targetState.id,
    methods.map((m) => ({
      id: uuid(),
      method: m.id,
      methodName: m.name,
    }))
  );
}
